// Money handling for the commission service.
//
// Every amount the service exchanges is an INTEGER NUMBER OF CENTS
// (`*_in_cents`). The rules here are absolute:
//
//   - Never store a float. Cents are integers, end to end.
//   - Never do currency arithmetic in floating point. `parseToCents` splits the
//     input on the decimal separator and assembles an integer from the two
//     halves, so `0.1 + 0.2` style drift cannot occur.
//   - Only convert to a decimal string at the very last moment, for display.
//
// ES5 only: no optional chaining, no template literals in hot paths.

var CENTS_PER_UNIT = 100;

function isBlank(value) {
  return value === null || value === undefined || value === '';
}

// Groups the integer part with thousands separators: 1234567 -> "1,234,567".
function groupThousands(digits) {
  return String(digits).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

// Formats an integer cents value for display: 123456 -> "1,234.56".
// Returns '' for blank input and null for values that are not finite numbers,
// so a bad field renders as nothing rather than as "NaN".
export function formatCents(value, options) {
  if (isBlank(value)) return '';

  var cents = Number(value);
  if (!isFinite(cents)) return null;

  var opts = options || {};
  // Cents are integers by contract; round defensively rather than truncate so a
  // stray float from the API does not silently lose a cent.
  var rounded = Math.round(cents);
  var negative = rounded < 0;
  var absolute = Math.abs(rounded);

  var whole = Math.floor(absolute / CENTS_PER_UNIT);
  var fraction = absolute % CENTS_PER_UNIT;

  var wholeText = opts.group === false
    ? String(whole)
    : groupThousands(whole);
  var fractionText = fraction < 10 ? '0' + fraction : String(fraction);

  var text = wholeText + '.' + fractionText;
  if (negative) text = '-' + text;
  if (opts.currency) text = opts.currency + ' ' + text;
  return text;
}

// Parses user input into an integer cents value without floating point.
// Accepts "1,234.56", " 1234.5 ", "-12", ".5", "1 234,56" (comma decimal).
// Returns null when the input is blank or not a valid amount, so callers can
// distinguish "nothing entered" from a real zero.
export function parseToCents(input) {
  if (isBlank(input)) return null;

  // A number already in major units (e.g. from a numeric input) is handled by
  // stringifying it, so the same digit-splitting path is used for everything.
  var text = String(input).trim();
  if (text === '') return null;

  var negative = /^-/.test(text);
  text = text.replace(/^[+-]/, '');

  // Strip spaces used as thousands separators.
  text = text.replace(/\s/g, '');

  // Decide which character is the decimal separator. If both are present the
  // last one wins ("1,234.56" -> '.', "1.234,56" -> ','); the other is treated
  // as a thousands separator and removed.
  var lastDot = text.lastIndexOf('.');
  var lastComma = text.lastIndexOf(',');
  var separator = lastDot > lastComma ? '.' : (lastComma > -1 ? ',' : '');

  if (separator) {
    var other = separator === '.' ? /,/g : /\./g;
    text = text.replace(other, '');
    text = text.replace(separator === '.' ? /\./g : /,/g, '.');
  }

  if (!/^\d*(\.\d*)?$/.test(text) || text === '' || text === '.') return null;

  var halves = text.split('.');
  var wholeText = halves[0] === '' ? '0' : halves[0];
  var fractionText = halves.length > 1 ? halves[1] : '';

  // Pad or truncate to exactly two decimal places. Truncation is deliberate:
  // a third decimal in a cents amount is not meaningful input.
  fractionText = (fractionText + '00').slice(0, 2);

  var cents = Number(wholeText) * CENTS_PER_UNIT + Number(fractionText);
  if (!isFinite(cents)) return null;

  return negative ? -cents : cents;
}

// True when the input parses to a valid amount. Blank is not valid — use this
// for required money fields and check for null separately where blank is ok.
export function isValidMoneyInput(input) {
  return parseToCents(input) !== null;
}

// Sums a list of cents values, ignoring blanks and non-numbers. Stays in
// integers throughout.
export function sumCents(values) {
  if (!values || !values.length) return 0;
  var total = 0;
  for (var i = 0; i < values.length; i += 1) {
    var cents = Number(values[i]);
    if (isFinite(cents)) total += Math.round(cents);
  }
  return total;
}

// Reads whichever cents field a commission-service payload happens to use.
// The three transaction families and the account ledger each name the amount
// differently; this is the single place that knows about that.
var CENTS_FIELDS = [
  'commission_amount_in_cents',
  'payout_amount_in_cents',
  'clawback_amount_in_cents',
  'current_commission_amount_in_cents',
  'total_net_commissions_amount_in_cents',
  'amount_in_cents'
];

export function readCents(record) {
  if (!record) return null;
  for (var i = 0; i < CENTS_FIELDS.length; i += 1) {
    var value = record[CENTS_FIELDS[i]];
    if (!isBlank(value)) return Number(value);
  }
  return null;
}
